// The subcategory rail's arrows.
//
// The row itself is an ordinary x scroller, so a swipe, a trackpad and a keyboard already work
// without this. The arrows are for a mouse on a desktop, where there is no gesture, and they are
// the same control the PDP gallery draws: a 34px circle that fades in when the rail is hovered.
//
// They hide rather than grey out at each end, and hide entirely when the row does not overflow,
// because an arrow that cannot do anything is worse than no arrow.
class ApsTileRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    this.buttons = [...this.querySelectorAll('[data-rail]')];
    if (!this.track || !this.buttons.length) return;

    this.sync = this.sync.bind(this);
    for (const b of this.buttons) {
      b.addEventListener('click', () => {
        // A page is the visible width less one tile, so the tile at the edge stays in view and
        // there is something to anchor on. Matches how the gallery rail reads.
        const step = Math.max(this.track.clientWidth - 140, 160);
        this.track.scrollBy({ left: step * Number(b.dataset.rail), behavior: 'smooth' });
        // Do not let the arrows' own state depend on the scroll event alone. A gesture always
        // fires one, but a programmatic scroll is not guaranteed to in every engine, and an arrow
        // stuck enabled at the end of the row is the visible failure. Sync again once the smooth
        // scroll has had time to land, and once more on scrollend where it exists.
        setTimeout(this.sync, 450);
      });
    }
    this.track.addEventListener('scroll', this.sync, { passive: true });
    this.track.addEventListener('scrollend', this.sync);

    // The row's width decides whether the arrows exist at all, and that changes with the viewport
    // and again when the lazy images land and the tiles settle.
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(this.sync);
      this.ro.observe(this.track);
    }
    window.addEventListener('resize', this.sync);
    this.sync();
  }

  disconnectedCallback() {
    this.ro?.disconnect();
    window.removeEventListener('resize', this.sync);
  }

  sync() {
    const t = this.track;
    // 2px of slack: a fractional scrollWidth is normal and would otherwise leave the next arrow
    // showing on a row with nowhere to go.
    const overflows = t.scrollWidth - t.clientWidth > 2;
    const atStart = t.scrollLeft <= 2;
    const atEnd = t.scrollLeft >= t.scrollWidth - t.clientWidth - 2;
    for (const b of this.buttons) {
      const back = Number(b.dataset.rail) < 0;
      b.disabled = !overflows || (back ? atStart : atEnd);
    }
  }
}
if (!customElements.get('aps-tile-rail')) customElements.define('aps-tile-rail', ApsTileRail);
